import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, KeyRound, ArrowLeft } from "lucide-react";
import { useNoIndex } from "@/lib/useNoIndex";  // PR #97 / R8
import { isAdminRole } from "@/lib/roles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_VERTICAL = "/logos/solara-vertical.png";

export default function ChangePasswordPage() {
  useNoIndex();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setIsLoading(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password changed. Please sign in with your new password.");
      // PR #97 / R10 bumped token_version on the backend — our current JWT
      // is already dead. PR #100 / AUM-9: also clears must_change_password.
      // Logout locally + send to /login so the next request uses a fresh
      // token issued against the new credentials.
      await logout();
      navigate("/login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[#fafaf9]">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_VERTICAL} alt="Solara Global" className="h-24 mx-auto" />
        </div>

        <div className="bg-white p-8 rounded shadow-sm border border-gray-200">
          <h1 className="font-heading text-2xl font-bold text-[#021d49] mb-2">Change Password</h1>
          <p className="text-gray-500 mb-6 text-sm">
            {user ? "Update your account password" : "Please log in first to change your password"}
          </p>

          {!user ? (
            <div className="text-center space-y-4">
              <p className="text-gray-600">You need to be logged in to change your password.</p>
              <p className="text-sm text-gray-500">If you've forgotten your password, please contact your administrator to reset it.</p>
              <Link to="/login" className="inline-flex items-center gap-2 text-[#bd9a59] hover:underline font-medium">
                <ArrowLeft className="w-4 h-4" /> Go to Login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label className="form-label">Current Password</Label>
                <div className="relative">
                  <Input type={showPasswords ? "text" : "password"} value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Enter current password"
                    className="form-input pr-10" required data-testid="current-password" />
                  <button type="button" onClick={() => setShowPasswords(!showPasswords)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="form-label">New Password</Label>
                <Input type={showPasswords ? "text" : "password"} value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)} placeholder="At least 6 characters"
                  className="form-input" required data-testid="new-password" />
              </div>
              <div>
                <Label className="form-label">Confirm New Password</Label>
                <Input type={showPasswords ? "text" : "password"} value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Repeat new password"
                  className="form-input" required data-testid="confirm-password" />
              </div>
              <button type="submit" disabled={isLoading} data-testid="change-password-submit"
                className="w-full bg-[#021d49] hover:bg-[#052b66] text-white py-3 rounded font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
                {isLoading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  : <><KeyRound className="w-5 h-5" /> Change Password</>}
              </button>
            </form>
          )}

          <div className="mt-4 text-center">
            <Link to={user ? (isAdminRole(user) ? "/admin" : "/my-applications") : "/login"}
              className="text-gray-400 text-sm hover:text-gray-600">
              <ArrowLeft className="w-3 h-3 inline mr-1" /> Back
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
